import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  AvatarBadge,
  Badge,
  Box,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  Tooltip,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { MdChatBubbleOutline, MdForum, MdGroupAdd, MdGroups, MdMarkChatUnread, MdSearch } from "react-icons/md";
import { AuthContext } from "context";
import chatServiceGlobal from "services/chatService/chat.service";
import useChat from "./useChat";
import Conversacion from "./Conversacion";
import ElegirPersonas from "./ElegirPersonas";
import { cuandoCorto } from "./formato";
import { resumenDeMensaje, vistaPrevia } from "./texto";

const normalizar = (valor) => String(valor || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

function AvatarConversacion({ conversacion, enLinea, tamano = "md" }) {
  if (conversacion.tipo === "grupo") {
    return <Avatar size={tamano} bg="brand.500" icon={<Icon as={MdGroups} boxSize="22px" color="white" />} />;
  }
  return (
    <Avatar size={tamano} name={conversacion.nombre}>
      {enLinea && <AvatarBadge boxSize="1em" bg="green.400" aria-label="En línea" />}
    </Avatar>
  );
}

function FilaConversacion({ conversacion, chat }) {
  const textoPrincipal = useColorModeValue("secondaryGray.900", "white");
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const escribe = chat.escribiendo[conversacion.id];
  const sinLeer = conversacion.noLeidos > 0;

  return (
    <Flex
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      align="center"
      gap="12px"
      px="10px"
      py="9px"
      borderRadius="12px"
      _hover={{ bg: fondoHover }}
      onClick={() => chat.abrir(conversacion)}
      aria-label={sinLeer ? `${conversacion.nombre}, ${conversacion.noLeidos} sin leer` : conversacion.nombre}
    >
      <AvatarConversacion conversacion={conversacion} enLinea={chat.enLinea.has(conversacion.contactoId)} />
      <Box flex="1" minW={0}>
        <Flex justify="space-between" align="baseline" gap="8px">
          <Text fontSize="sm" fontWeight={sinLeer ? "800" : "600"} color={textoPrincipal} noOfLines={1}>{conversacion.nombre}</Text>
          <Text fontSize="11px" color={sinLeer ? "brand.500" : textoSecundario} flexShrink={0}>{cuandoCorto(conversacion.fecha)}</Text>
        </Flex>
        <Flex justify="space-between" align="center" gap="8px">
          <Text
            fontSize="xs"
            color={escribe ? "brand.500" : textoSecundario}
            fontWeight={sinLeer && !escribe ? "600" : "400"}
            fontStyle={conversacion.ultimoMensaje?.tipo === "sistema" ? "italic" : "normal"}
            noOfLines={1}
          >
            {escribe
              ? (conversacion.tipo === "grupo" ? `${escribe.nombre.split(" ")[0]} está escribiendo…` : "Escribiendo…")
              : vistaPrevia(conversacion, chat.usuarioId)}
          </Text>
          {sinLeer && (
            <Badge bg="brand.500" color="white" borderRadius="full" minW="20px" textAlign="center" flexShrink={0}>
              {conversacion.noLeidos > 99 ? "99+" : conversacion.noLeidos}
            </Badge>
          )}
        </Flex>
      </Box>
    </Flex>
  );
}

function FilaPersona({ usuario, enLinea, onAbrir }) {
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.100");
  return (
    <Flex
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      align="center"
      gap="12px"
      px="10px"
      py="7px"
      borderRadius="12px"
      _hover={{ bg: fondoHover }}
      onClick={() => onAbrir(usuario)}
      aria-label={`Escribir a ${usuario.nombre}`}
    >
      <Avatar size="sm" name={usuario.nombre}>
        {enLinea && <AvatarBadge boxSize="1em" bg="green.400" />}
      </Avatar>
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight="600" noOfLines={1}>{usuario.nombre}</Text>
        <Text fontSize="xs" color={textoSecundario} noOfLines={1}>{usuario.rol || usuario.correo}</Text>
      </Box>
    </Flex>
  );
}

function ListaConversaciones({ chat, onNuevoGrupo }) {
  const [busqueda, setBusqueda] = useState("");
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const colorTitulo = useColorModeValue("secondaryGray.900", "white");
  const bordes = useColorModeValue("gray.200", "whiteAlpha.200");

  const buscado = normalizar(busqueda);
  const conversaciones = useMemo(() => chat.conversaciones.filter((conversacion) => !buscado
    || normalizar(conversacion.nombre).includes(buscado)
    || conversacion.participantes.some((participante) => normalizar(participante.nombre).includes(buscado))),
  [chat.conversaciones, buscado]);

  // Quien ya tiene una conversación directa aparece arriba; aquí solo los demás.
  const personas = useMemo(() => {
    const conDirecta = new Set(chat.conversaciones.filter((c) => c.tipo === "directa").map((c) => c.contactoId));
    return (chat.usuarios || [])
      .filter((usuario) => !conDirecta.has(usuario.id))
      .filter((usuario) => !buscado || normalizar(`${usuario.nombre} ${usuario.correo} ${usuario.rol}`).includes(buscado))
      .sort((a, b) => Number(chat.enLinea.has(b.id)) - Number(chat.enLinea.has(a.id)));
  }, [chat.usuarios, chat.conversaciones, chat.enLinea, buscado]);

  return (
    <Flex direction="column" h="100%" minH={0}>
      <Box px="16px" pt="16px" pb="10px" borderBottom="1px solid" borderColor={bordes}>
        <Flex align="center" justify="space-between" pe="36px" mb="10px">
          <Flex align="center" gap="8px">
            <Text fontSize="lg" fontWeight="700" color={colorTitulo}>Chat interno</Text>
            {chat.noLeidos > 0 && <Badge colorScheme="red" borderRadius="full">{chat.noLeidos}</Badge>}
          </Flex>
          <Tooltip label="Nuevo grupo" hasArrow openDelay={300}>
            <IconButton aria-label="Nuevo grupo" icon={<MdGroupAdd />} variant="ghost" size="sm" onClick={onNuevoGrupo} />
          </Tooltip>
        </Flex>
        <DrawerCloseButton top="16px" />
        {chat.estado !== "conectado" && chat.estado !== "conectando" && (
          <Text fontSize="xs" color="orange.400" mb="8px">
            {{
              cerrado: "Tu sesión expiró. Vuelve a iniciar sesión para usar el chat.",
              no_configurado: "El chat interno no está configurado en el servidor.",
            }[chat.estado] || "Sin conexión. Reconectando…"}
          </Text>
        )}
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none"><MdSearch /></InputLeftElement>
          <Input
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
            placeholder="Buscar conversación o persona"
            aria-label="Buscar conversación o persona"
            borderRadius="10px"
          />
        </InputGroup>
      </Box>

      <Box flex="1" minH={0} overflowY="auto" px="8px" py="8px">
        {!chat.listaCargada ? (
          <Flex direction="column" gap="8px" px="8px">
            <Skeleton h="52px" borderRadius="12px" />
            <Skeleton h="52px" borderRadius="12px" />
          </Flex>
        ) : (
          <>
            {conversaciones.map((conversacion) => (
              <FilaConversacion key={conversacion.id} conversacion={conversacion} chat={chat} />
            ))}
            {!conversaciones.length && !busqueda && (
              <Flex direction="column" align="center" textAlign="center" py="18px" px="16px" color={textoSecundario} gap="4px">
                <Icon as={MdForum} boxSize="34px" />
                <Text fontSize="sm" fontWeight="600">Aún no tienes conversaciones</Text>
                <Text fontSize="xs">Escríbele a alguien del equipo o crea un grupo.</Text>
              </Flex>
            )}
            {personas.length > 0 && (
              <>
                <Text fontSize="xs" fontWeight="700" color={textoSecundario} textTransform="uppercase" letterSpacing="0.04em" px="10px" mt="14px" mb="4px">
                  Escribir a
                </Text>
                {personas.map((usuario) => (
                  <FilaPersona key={usuario.id} usuario={usuario} enLinea={chat.enLinea.has(usuario.id)} onAbrir={chat.abrirCon} />
                ))}
              </>
            )}
            {busqueda && !conversaciones.length && !personas.length && (
              <Text fontSize="sm" color={textoSecundario} textAlign="center" py="18px">Nadie coincide con la búsqueda.</Text>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
}

function ChatActivo({ usuarioId, limites }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const personas = useDisclosure();
  const [modoPersonas, setModoPersonas] = useState("crear");
  const toast = useToast();
  const tamano = useBreakpointValue({ base: "full", md: "md" });
  const chatRef = useRef(null);

  const navbarIcon = useColorModeValue("gray.500", "white");
  const fondoHover = useColorModeValue("gray.100", "whiteAlpha.200");
  const menuBg = useColorModeValue("white", "navy.800");
  const fondoAviso = useColorModeValue("white", "navy.700");
  const textoAviso = useColorModeValue("gray.600", "gray.300");

  const abrirDesdeAviso = useCallback((mensaje, conversacion) => {
    onOpen();
    if (conversacion) chatRef.current?.abrir(conversacion);
    else chatRef.current?.abrirCon({ id: mensaje.usuarioId, nombre: mensaje.autor });
  }, [onOpen]);

  // Un mensaje que no se está viendo: aviso con quién escribe; al tocarlo se abre.
  const avisar = useCallback((mensaje, conversacion) => {
    const enGrupo = conversacion?.tipo === "grupo";
    const id = `chat-${mensaje.conversacionId}`;
    const aviso = {
      position: "top-right",
      duration: 6000,
      render: ({ onClose: cerrarAviso }) => (
        <Flex
          as="button"
          type="button"
          onClick={() => {
            cerrarAviso();
            abrirDesdeAviso(mensaje, conversacion);
          }}
          gap="10px"
          align="center"
          textAlign="left"
          w="100%"
          bg={fondoAviso}
          p="12px"
          borderRadius="14px"
          boxShadow="lg"
          borderLeft="4px solid"
          borderColor="brand.500"
        >
          <Avatar size="sm" name={mensaje.autor} />
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="700" noOfLines={1}>
              {enGrupo ? `${mensaje.autor} en ${conversacion.nombre}` : mensaje.autor}
            </Text>
            <Text fontSize="xs" color={textoAviso} noOfLines={2}>{resumenDeMensaje(mensaje)}</Text>
          </Box>
        </Flex>
      ),
    };
    // Varios mensajes seguidos de la misma conversación: un solo aviso, con el último.
    if (toast.isActive(id)) toast.update(id, aviso);
    else toast({ id, ...aviso });
  }, [toast, abrirDesdeAviso, fondoAviso, textoAviso]);

  const chat = useChat({ usuarioId, visible: isOpen, alMensajeNuevo: avisar });
  chatRef.current = chat;

  const abrirPanel = () => {
    onOpen();
    if (!chat.usuarios) chat.cargarUsuarios();
  };

  const elegirPersonas = (modo) => {
    setModoPersonas(modo);
    if (!chat.usuarios) chat.cargarUsuarios();
    personas.onOpen();
  };

  const confirmarPersonas = ({ nombre, usuarioIds }) => (modoPersonas === "crear"
    ? chat.crearGrupo({ nombre, usuarioIds })
    : chat.agregarAGrupo(usuarioIds));

  const total = chat.noLeidos;

  return (
    <>
      <Box position="relative">
        <Tooltip label="Chat interno" hasArrow openDelay={400}>
          <IconButton
            aria-label={total ? `Chat interno, ${total} sin leer` : "Chat interno"}
            icon={<Icon as={total ? MdMarkChatUnread : MdChatBubbleOutline} boxSize={{ base: "20px", md: "22px" }} />}
            variant="ghost"
            borderRadius="full"
            w={{ base: "38px", md: "42px" }}
            h={{ base: "38px", md: "42px" }}
            minW="auto"
            color={navbarIcon}
            _hover={{ bg: fondoHover }}
            onClick={abrirPanel}
          />
        </Tooltip>
        {total > 0 && (
          <Flex
            position="absolute"
            top="2px"
            right="0px"
            minW="18px"
            h="18px"
            px="4px"
            borderRadius="999px"
            bg="red.500"
            color="white"
            fontSize="10px"
            fontWeight="700"
            align="center"
            justify="center"
            borderWidth="2px"
            borderColor={menuBg}
            pointerEvents="none"
            data-testid="contador-chat"
          >
            {total > 99 ? "99+" : total}
          </Flex>
        )}
      </Box>

      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size={tamano || "md"} returnFocusOnClose={false}>
        <DrawerOverlay />
        <DrawerContent bg={menuBg} maxH="100dvh">
          {chat.hilo ? (
            <Conversacion chat={chat} limites={limites} onAgregarPersonas={() => elegirPersonas("agregar")} />
          ) : (
            <ListaConversaciones chat={chat} onNuevoGrupo={() => elegirPersonas("crear")} />
          )}
        </DrawerContent>
      </Drawer>

      <ElegirPersonas
        isOpen={personas.isOpen}
        onClose={personas.onClose}
        modo={modoPersonas}
        usuarios={chat.usuarios}
        excluir={modoPersonas === "agregar" ? (chat.hilo?.participantes || []).map((participante) => participante.id) : []}
        alConfirmar={confirmarPersonas}
      />
    </>
  );
}

/**
 * El chat solo aparece si el servidor tiene dónde guardarlo (el almacén en Cloudflare).
 * De paso trae qué archivos acepta y hasta qué tamaño.
 */
export default function ChatInterno() {
  const { user } = useContext(AuthContext);
  const usuarioId = Number(user?.USU_NID) || null;
  const [estado, setEstado] = useState(null);

  useEffect(() => {
    if (!usuarioId) return undefined;
    let vigente = true;
    chatServiceGlobal.estado().then((respuesta) => {
      if (vigente && !respuesta?.error) setEstado(respuesta?.data || null);
    });
    return () => {
      vigente = false;
    };
  }, [usuarioId]);

  if (!usuarioId || !estado?.configurado) return null;
  return <ChatActivo usuarioId={usuarioId} limites={estado} />;
}
